/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Config from '../../config';
import { Job } from '../../menu/jobsMenu/types';
import { BATCH_REPORT_ITEM_TYPES } from '../../administration/customGroupsPage/constants';
import Select from '../../shared/reactSelect/select.component';
import { ReduxAlert } from '../../shared/alert.component';
import { useAddJob } from '../../shared/jim/hooks';
import AlertWarning from '../../shared/alertWarning.component';
import Button from '../../shared/button.component';
import {
  StandardReportDefinition,
  CustomReportDefinition,
  BatchReportParameters,
} from '../../shared/types/batchReports';
import { SftpSchedule } from '@common/types/sftpSchedule';
import { ScheduleRecurrenceEditor, ScheduleSummary } from './scheduleEditor';
import Spinner from '@client-shared/spinner.component';
import { ParamValues } from '../../shared/types/customReports';
import { saveArchiveReport } from '../../shared/store/actions';
import { environmentSuffixSelector, currentPeriodSelector } from '../../shared/store/selectors';
import { formattingOptionsSelector } from '../../userData/store/selectors';
import { verifyParamOptionsQuery } from '../customReports/store/actions';
import { useBatchReportDetails } from './useBatchReportDetails.hook';
import { StandardReportRow } from './standardReportRow.component';
import { CustomReportRow } from './customReportRow.component';
import { useBatchReportsFromLocalStorage } from './localStorage';
import {
  areAllValuesSelectedForBatchReport,
  getMergedBatchReportParameters,
  generateExportPayload,
  getReportsForExport,
  generateArchivalPayload,
  isValidPayloadLimit,
} from './helpers';
import {
  useTooManyExportedTabsWarning,
  TooManyExportedTabsWarning,
} from './useTooManyExportedTabsWarning.hook';
import {
  CustomReportOption,
  SyncParamOptionData,
  AllParamOptions,
  EntityOption,
  ExportFormat,
} from './types';
import { useCompanyCodeOptions } from './useCompanyCodeOptions.hook';
import { useBatchReportValidation } from './useBatchReportValidation.hook';
import ValidationConfirmationModal from '../customReports/validationConfirmationModal.component';

const DEFAULT_SFTP_SCHEDULE: SftpSchedule = {
  enabled: false,
  timezone: 'America/New_York',
  runHourLocal: 8,
  recurrence: { pattern: 'daily' },
};

type BatchReportDetailsProps = {
  selectedBatchReport: CustomReportOption,
  onAddJobSuccess: (job: Job) => void,
  onAddJobError: (error: Error) => void,
  onExportStart: () => void,
  isExportInProgress: boolean,
  isEditEnabled: boolean,
  setIsEditEnabled: () => void,
};

export const BatchReportDetails = ({
  selectedBatchReport,
  onAddJobError,
  onAddJobSuccess,
  onExportStart,
  isExportInProgress,
  isEditEnabled,
  setIsEditEnabled,
}: BatchReportDetailsProps) => {
  const dispatch = useDispatch();
  const currentPeriod = useSelector(currentPeriodSelector);
  const environmentSuffix = useSelector(environmentSuffixSelector);
  const formattingOptions = useSelector(formattingOptionsSelector);

  const [paramOptions, setParamOptions] = useState<AllParamOptions>({});

  const [
    localStorageBatchReportParameters,
    setLocalStorageBatchReportParameters,
  ] = useBatchReportsFromLocalStorage(selectedBatchReport.value);

  const addJob = useAddJob({
    jobType: Config.jimJobs.batchReportsExport.type,
    onError: onAddJobError,
    onSuccess: onAddJobSuccess,
  });

  const addArchiveJob = useAddJob({
    jobType: Config.jimJobs.batchArchiveReportsExport.type,
    onError: onAddJobError,
    onSuccess: onAddJobSuccess,
  });

  const {
    archivalFlag,
    sftpDeliveryFlag,
    batchReportDetails,
    initialBatchReportParameters,
    isAllowedToEdit,
    isFetchingReportDetails,
    reports,
    role,
    savedExportFormat,
    sftpSchedule: savedSftpSchedule,
    refetchBatchReportDetails,
  } = useBatchReportDetails(selectedBatchReport.value);

  const [exportFormat, setExportFormat] = useState<ExportFormat>(savedExportFormat);

  useEffect(() => {
    setExportFormat(savedExportFormat);
  }, [savedExportFormat]);

  const [editingSftpSchedule, setEditingSftpSchedule] = useState<SftpSchedule>(
    savedSftpSchedule ?? DEFAULT_SFTP_SCHEDULE
  );

  useEffect(() => {
    setEditingSftpSchedule(savedSftpSchedule ?? DEFAULT_SFTP_SCHEDULE);
  }, [savedSftpSchedule]);

  const restrictCompanyCodeAndDefaults = archivalFlag || sftpDeliveryFlag;

  const {
    companyCode,
    setCompanyCode,
    companyCodeOptions,
    isLoadingCompanyCodeOptions,
  } = useCompanyCodeOptions(batchReportDetails);

  const batchReportParameters = useMemo<BatchReportParameters>(
    () => getMergedBatchReportParameters(initialBatchReportParameters, localStorageBatchReportParameters, {
      useDefaults: restrictCompanyCodeAndDefaults && !isEditEnabled,
    }),
    [initialBatchReportParameters, localStorageBatchReportParameters, restrictCompanyCodeAndDefaults, isEditEnabled]
  );

  const verifyParamQueryWrapper = useCallback(
    (params: any) => {
      dispatch(verifyParamOptionsQuery({ name: '', ...params }));
    },
    [dispatch]
  );

  const {
    showValidationModal,
    validationMessage,
    runValidators,
    handleValidationCancel,
    handleValidationProceed,
  } = useBatchReportValidation({
    batchReportParameters,
    verifyParamOptionsQuery: verifyParamQueryWrapper,
  });

  const isExportDisabled = useMemo(() => {
    if (!areAllValuesSelectedForBatchReport(batchReportParameters, archivalFlag)) {
      return true;
    }
    if (archivalFlag && !companyCode) {
      return true;
    }
    return false;
  }, [archivalFlag, batchReportParameters, companyCode]);

  const isParamOptionsLoading = useMemo(() => {
    const reportNames = reports.filter(r => r.itemType === BATCH_REPORT_ITEM_TYPES.REPORTS).map(r => r.itemName);
    if (!reportNames) {
      return false;
    }
    const params = reportNames.map(r => Object.prototype.hasOwnProperty.call(paramOptions, r));
    return params.some(param => !param);
  }, [reports, paramOptions]);

  const {
    areTooManyExportedTabs,
    numberOfExportedTabs,
  } = useTooManyExportedTabsWarning(batchReportParameters);

  const syncParamOptions = useCallback((reportName: string, data: SyncParamOptionData) => {
    setParamOptions(prev => ({
      ...prev,
      [reportName]: {
        ...prev[reportName] ?? {},
        ...data.options,
      },
    }));
  }, []);

  const updateCompanyCode = (newCompanyCode: EntityOption) => {
    setCompanyCode(newCompanyCode);
  };

  const updateReportParamValues = useCallback((itemName: string, newReportParamValues: ParamValues) => {
    setLocalStorageBatchReportParameters({
      ...localStorageBatchReportParameters,
      [itemName]: {
        ...localStorageBatchReportParameters[itemName],
        reportParamValues: newReportParamValues,
      },
    });
  }, [setLocalStorageBatchReportParameters, localStorageBatchReportParameters]);

  const updateIncludedInExport = useCallback((itemName: string, newValue: boolean) => {
    setLocalStorageBatchReportParameters({
      ...localStorageBatchReportParameters,
      [itemName]: {
        ...localStorageBatchReportParameters[itemName],
        isIncludedInExport: newValue,
      },
    });
  }, [setLocalStorageBatchReportParameters, localStorageBatchReportParameters]);

  const reportsForExport = useMemo(() => {
    if (!batchReportDetails || !batchReportParameters) {
      return null;
    }
    return getReportsForExport(batchReportParameters, formattingOptions, paramOptions);
  }, [batchReportDetails, batchReportParameters, formattingOptions, paramOptions]);

  const saveBatchReport = async () => {
    if (!batchReportDetails || !batchReportParameters) {
      return;
    }
    const reportValues: { [k: string]: ParamValues } = {};
    Object.keys(batchReportParameters).map(param => reportValues[param]
      = batchReportParameters[param].reportParamValues);
    const payload: Record<string, unknown> = {
      batchId: batchReportDetails.id,
      archivalFlag,
      sftpDeliveryFlag,
      companyCode,
      reports: reportValues,
      exportFormat,
    };
    if (sftpDeliveryFlag) {
      const { lastScheduledRunAt: _lastRunIgnored, ...scheduleToSave } = editingSftpSchedule;
      payload.sftpSchedule = scheduleToSave;
      payload.reportsForExport = reportsForExport;
    }
    try {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await (dispatch as any)(saveArchiveReport(batchReportDetails.id, payload));
      await refetchBatchReportDetails();
    } catch {
      return;
    }
    setIsEditEnabled();
  };

  const cancelBatchReportParameters = () => {
    setIsEditEnabled();
    setLocalStorageBatchReportParameters(initialBatchReportParameters);
    setCompanyCode(companyCode);
    setEditingSftpSchedule(savedSftpSchedule ?? DEFAULT_SFTP_SCHEDULE);
  };

  const resetBatchReportParameters = () => {
    setLocalStorageBatchReportParameters(initialBatchReportParameters);
    setCompanyCode(companyCode);
  };
  const archivalPayload = useMemo(() => {
    if (!reportsForExport || !batchReportDetails || !companyCode) {
      return null;
    }

    return generateArchivalPayload({
      batchId: batchReportDetails.id,
      batchName: batchReportDetails.name,
      reports: reportsForExport,
      entity: companyCode,
      reportName: selectedBatchReport.label,
      sftpDeliveryFlag: batchReportDetails.sftpDeliveryFlag,
      exportFormat,
    });
  }, [
    reportsForExport,
    batchReportDetails,
    companyCode,
    selectedBatchReport.label,
    exportFormat,
  ]);

  const exportAchivalReport = useCallback(async () => {
    if (!archivalPayload) {
      return;
    }

    runValidators(() => {
      onExportStart();
      addArchiveJob(archivalPayload);
    });
  }, [
    archivalPayload,
    addArchiveJob,
    onExportStart,
    runValidators,
  ]);

  const reportPayload = useMemo(() => {
    if (!reportsForExport || !batchReportDetails) {
      return null;
    }

    return generateExportPayload({
      batchId: batchReportDetails.id,
      batchName: batchReportDetails.name,
      environmentSuffix,
      reports: reportsForExport,
      sftpDeliveryFlag: batchReportDetails.sftpDeliveryFlag,
      exportFormat,
    });
  }, [
    reportsForExport,
    batchReportDetails,
    environmentSuffix,
    exportFormat,
  ]);

  const exportBatchReport = useCallback(async () => {
    if (!reportPayload) {
      return;
    }

    runValidators(() => {
      onExportStart();

      if (reportPayload.reports[0].data.params?.periodName === 'current_period') {
        const newReportPayload = reportPayload;
        newReportPayload.reports[0].data.params.periodName = currentPeriod.name;
        addJob(newReportPayload);
      } else {
        addJob(reportPayload);
      }
    });
  }, [
    reportPayload,
    addJob,
    onExportStart,
    currentPeriod,
    runValidators,
  ]);

  const renderReportRows = useCallback(() => reports.map(({ itemName, itemLabel, itemType, reportDefinition }) => {
    const reportParamValues = batchReportParameters[itemName]?.reportParamValues || null;
    const isIncludedInExport = batchReportParameters[itemName]?.isIncludedInExport || false;
    if (itemType === BATCH_REPORT_ITEM_TYPES.CUSTOM_REPORTS) {
      return (
        <CustomReportRow
          key={itemName}
          reportName={itemLabel}
          reportType={itemType}
          reportDefinition={reportDefinition as CustomReportDefinition}
          reportParamValues={reportParamValues}
          isIncludedInExport={isIncludedInExport}
          updateReportParamValues={updateReportParamValues}
          updateIncludedInExport={updateIncludedInExport}
          archivalFlag={archivalFlag}
          restrictBatchDefaults={restrictCompanyCodeAndDefaults}
          userRole={`${role}`}
          editable={isEditEnabled}
        />
      );
    }
    if (itemType === BATCH_REPORT_ITEM_TYPES.REPORTS) {
      return (
        <StandardReportRow
          key={itemName}
          reportLabel={itemLabel}
          reportName={itemName}
          reportType={itemType}
          reportDefinition={reportDefinition as StandardReportDefinition}
          reportParamValues={reportParamValues}
          isIncludedInExport={isIncludedInExport}
          updateReportParamValues={updateReportParamValues}
          updateIncludedInExport={updateIncludedInExport}
          syncParamOptions={syncParamOptions}
          archivalFlag={archivalFlag}
          restrictBatchDefaults={restrictCompanyCodeAndDefaults}
          userRole={`${role}`}
          editable={isEditEnabled}
        />
      );
    }
    return null;
  }), [
    reports,
    batchReportParameters,
    archivalFlag,
    restrictCompanyCodeAndDefaults,
    role,
    isEditEnabled,
    updateReportParamValues,
    updateIncludedInExport,
    syncParamOptions,
  ]);

  if (isFetchingReportDetails || !batchReportParameters) {
    return <Spinner />;
  }

  return (
    <>
      <ReduxAlert />
      <TooManyExportedTabsWarning
        areTooManyExportedTabs={areTooManyExportedTabs}
        numberOfExportedTabs={numberOfExportedTabs}
      />
      <AlertWarning
        warningMessage="Batch Report contains too many selected reports and it is possible that it won't run successfully."
        show={isValidPayloadLimit(archivalFlag ? archivalPayload : reportPayload)}
      />
      {archivalFlag && (
        <AlertWarning
          warningMessage="Archive Reports should be executed by users with access to ALL company codes in the client to ensure complete and accurate archival data."
          show={true}
        />
      )}
      {sftpDeliveryFlag && (
        <AlertWarning
          warningMessage="SFTP Reports should be executed by users with access to ALL company codes in the client to ensure complete and accurate reporting."
          show={true}
        />
      )}
      <ValidationConfirmationModal
        isVisible={showValidationModal}
        validationMessage={validationMessage}
        onCancel={handleValidationCancel}
        onProceed={handleValidationProceed}
      />
      {archivalFlag && (
        <div className="row no-gutters page-content-container">
          <div className="no-gutters batch-report-params-container">
            <div className="custom-report-params-container">
              <div className="custom-param-selector">
                <div className="custom-param-label">Enter Connection / Company Code / Custom Group</div>
                <Select
                  className="select-custom-reports"
                  clearable={false}
                  disabled={isLoadingCompanyCodeOptions || companyCodeOptions.length === 0}
                  options={companyCodeOptions}
                  value={companyCode}
                  placeholder="Enter Connection / Company Code / Custom Group"
                  onChange={updateCompanyCode}
                  isLoading={isLoadingCompanyCodeOptions}
                  virtualized
                  closeOnSelect
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="row no-gutters page-content-container">
        <div className="no-gutters batch-report-params-container">
          <div className="custom-report-params-container">
            <div className="custom-param-selector">
              <div className="custom-param-label">Export Format</div>
              <Select
                className="select-custom-reports"
                clearable={false}
                options={[
                  { value: 'xlsx', label: 'Excel (.xlsx)' },
                  { value: 'csv', label: 'CSV (.csv)' },
                ]}
                value={exportFormat}
                onChange={(option: { value: ExportFormat }) => setExportFormat(option.value)}
                closeOnSelect
              />
            </div>
          </div>
        </div>
      </div>
      {sftpDeliveryFlag && !archivalFlag && isEditEnabled && (
        <div className="row no-gutters page-content-container">
          <div className="no-gutters batch-report-params-container">
            <ScheduleRecurrenceEditor
              schedule={editingSftpSchedule}
              onChange={setEditingSftpSchedule}
            />
          </div>
        </div>
      )}
      {sftpDeliveryFlag && !archivalFlag && !isEditEnabled && (
        <div className="row no-gutters page-content-container">
          <div className="no-gutters batch-report-params-container">
            <ScheduleSummary schedule={savedSftpSchedule} />
          </div>
        </div>
      )}
      {renderReportRows()}
      <div className="col text-right">
        {isEditEnabled && (
          <Button
            onClick={saveBatchReport}
            kind="secondary"
            className="mr-2"
          >
            Save
          </Button>
        )}
        {isEditEnabled && (
          <Button
            onClick={cancelBatchReportParameters}
            kind="secondary"
            disabled={isExportInProgress}
            className="mr-2"
          >
            Cancel
          </Button>
        )}
        {restrictCompanyCodeAndDefaults && isAllowedToEdit && !isEditEnabled && (
          <Button
            onClick={setIsEditEnabled}
            kind="secondary"
            className="mr-2"
          >
            Edit Defaults
          </Button>
        )}
        {!restrictCompanyCodeAndDefaults && (
          <Button
            onClick={resetBatchReportParameters}
            kind="secondary"
            disabled={isExportInProgress}
            className="mr-2"
          >
            Reset
          </Button>
        )}
        <Button
          onClick={archivalFlag ? exportAchivalReport : exportBatchReport}
          kind="secondary"
          disabled={isExportDisabled
            || areTooManyExportedTabs
            || (restrictCompanyCodeAndDefaults && isAllowedToEdit && isEditEnabled)
            || isParamOptionsLoading
            || (archivalFlag && isLoadingCompanyCodeOptions)}
          isLoading={isExportInProgress}
        >
          Export
        </Button>
      </div>
    </>
  );
};
